import React, { useState, useEffect } from 'react';
import '../crm.css';

const typeOptions = [
    { value: 'customer', label: 'Customer' },
    { value: 'prospect', label: 'Prospect' },
    { value: 'partner', label: 'Partner' },
    { value: 'vendor', label: 'Vendor' },
    { value: 'other', label: 'Other' }
];

const customerTypeOptions = [
    { value: 'buyer', label: 'Buyer' },
    { value: 'seller', label: 'Seller' },
    { value: 'investor', label: 'Investor' },
    { value: 'tenant', label: 'Tenant' },
    { value: 'landlord', label: 'Landlord' },
    { value: 'other', label: 'Other' },
];

const lifecycleOptions = [
    { value: 'new', label: 'New' },
    { value: 'active', label: 'Active' },
    { value: 'hot', label: 'Hot' },
    { value: 'cold', label: 'Cold' },
    { value: 'closed', label: 'Closed' },
    { value: 'dormant', label: 'Dormant' },
];

const timelineOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: '1_month', label: '1 Month' },
    { value: '3_months', label: '3 Months' },
    { value: '6_months', label: '6 Months' },
    { value: 'flexible', label: 'Flexible' },
];

const ContactForm = ({ contact, onSubmit, onCancel, loading }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        mobile: '',
        company: '',
        title: '',
        department: '',
        type: 'customer',
        address: '',
        city: '',
        state: '',
        country: '',
        notes: '',
        customerType: '',
        lifecycleStage: '',
        language: '',
        preferredChannel: '',
        rating: '',
        source: '',
        nextFollowUpAt: '',
        requirements: {
            purpose: '',
            propertyTypes: [],
            budgetMin: '',
            budgetMax: '',
            locations: [],
            projects: [],
            bhk: [],
            areaMin: '',
            areaMax: '',
            timeline: '',
            mustHaves: [],
            dealBreakers: [],
        },
    });

    const [errors, setErrors] = useState({});
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [requirementsText, setRequirementsText] = useState({
        propertyTypes: '',
        locations: '',
        projects: '',
        bhk: '',
        mustHaves: '',
        dealBreakers: '',
    });

    useEffect(() => {
        if (contact) {
            const req = contact.requirements || {};
            setFormData({
                firstName: contact.firstName || '',
                lastName: contact.lastName || '',
                email: contact.email || '',
                phone: contact.phone || '',
                mobile: contact.mobile || '',
                company: contact.company || '',
                title: contact.title || '',
                department: contact.department || '',
                type: contact.type || 'customer',
                address: contact.address || '',
                city: contact.city || '',
                state: contact.state || '',
                country: contact.country || '',
                notes: contact.notes || '',
                customerType: contact.customerType || '',
                lifecycleStage: contact.lifecycleStage || '',
                language: contact.language || '',
                preferredChannel: contact.preferredChannel || '',
                rating: contact.rating || '',
                source: contact.source || '',
                nextFollowUpAt: contact.nextFollowUpAt ? contact.nextFollowUpAt.split('T')[0] : '',
                requirements: {
                    purpose: req.purpose || '',
                    propertyTypes: req.propertyTypes || [],
                    budgetMin: req.budgetMin || '',
                    budgetMax: req.budgetMax || '',
                    locations: req.locations || [],
                    projects: req.projects || [],
                    bhk: req.bhk || [],
                    areaMin: req.areaMin || '',
                    areaMax: req.areaMax || '',
                    timeline: req.timeline || '',
                    mustHaves: req.mustHaves || [],
                    dealBreakers: req.dealBreakers || [],
                },
            });
            setRequirementsText({
                propertyTypes: (req.propertyTypes || []).join(', '),
                locations: (req.locations || []).join(', '),
                projects: (req.projects || []).join(', '),
                bhk: (req.bhk || []).join(', '),
                mustHaves: (req.mustHaves || []).join(', '),
                dealBreakers: (req.dealBreakers || []).join(', '),
            });
        }
    }, [contact]);

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            const normalizeList = (value) =>
                value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            onSubmit({
                ...formData,
                rating: formData.rating ? Number(formData.rating) : undefined,
                nextFollowUpAt: formData.nextFollowUpAt ? new Date(formData.nextFollowUpAt).toISOString() : undefined,
                requirements: {
                    ...formData.requirements,
                    propertyTypes: normalizeList(requirementsText.propertyTypes),
                    locations: normalizeList(requirementsText.locations),
                    projects: normalizeList(requirementsText.projects),
                    bhk: normalizeList(requirementsText.bhk),
                    mustHaves: normalizeList(requirementsText.mustHaves),
                    dealBreakers: normalizeList(requirementsText.dealBreakers),
                    budgetMin: formData.requirements.budgetMin ? Number(formData.requirements.budgetMin) : undefined,
                    budgetMax: formData.requirements.budgetMax ? Number(formData.requirements.budgetMax) : undefined,
                    areaMin: formData.requirements.areaMin ? Number(formData.requirements.areaMin) : undefined,
                    areaMax: formData.requirements.areaMax ? Number(formData.requirements.areaMax) : undefined,
                },
            });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="crm-form">
            <div className="crm-form-row">
                <div className="crm-form-group">
                    <label className="crm-label required">First Name</label>
                    <input
                        type="text"
                        className={`crm-input ${errors.firstName ? 'error' : ''}`}
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        disabled={loading}
                    />
                    {errors.firstName && <span className="crm-error">{errors.firstName}</span>}
                </div>
                <div className="crm-form-group">
                    <label className="crm-label required">Last Name</label>
                    <input
                        type="text"
                        className={`crm-input ${errors.lastName ? 'error' : ''}`}
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        disabled={loading}
                    />
                    {errors.lastName && <span className="crm-error">{errors.lastName}</span>}
                </div>
            </div>

            <div className="crm-form-row">
                <div className="crm-form-group">
                    <label className="crm-label required">Email</label>
                    <input
                        type="email"
                        className={`crm-input ${errors.email ? 'error' : ''}`}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        disabled={loading}
                    />
                    {errors.email && <span className="crm-error">{errors.email}</span>}
                </div>
                <div className="crm-form-group">
                    <label className="crm-label">Type</label>
                    <select
                        className="crm-select"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        disabled={loading}
                    >
                        {typeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="crm-form-row">
                <div className="crm-form-group">
                    <label className="crm-label">Phone</label>
                    <input
                        type="tel"
                        className="crm-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        disabled={loading}
                    />
                </div>
                <div className="crm-form-group">
                    <label className="crm-label">Mobile</label>
                    <input
                        type="tel"
                        className="crm-input"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="crm-form-row">
                <div className="crm-form-group">
                    <label className="crm-label">Company</label>
                    <input
                        type="text"
                        className="crm-input"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        disabled={loading}
                    />
                </div>
                <div className="crm-form-group">
                    <label className="crm-label">Title</label>
                    <input
                        type="text"
                        className="crm-input"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="crm-form-row">
                <div className="crm-form-group">
                    <label className="crm-label">Department</label>
                    <input
                        type="text"
                        className="crm-input"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="crm-form-group">
                <label className="crm-label">Address</label>
                <input
                    type="text"
                    className="crm-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street Address"
                    disabled={loading}
                    style={{ marginBottom: '8px' }}
                />
                <div className="crm-form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <input
                        type="text"
                        className="crm-input"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="City"
                        disabled={loading}
                    />
                    <input
                        type="text"
                        className="crm-input"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        placeholder="State"
                        disabled={loading}
                    />
                    <input
                        type="text"
                        className="crm-input"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        placeholder="Country"
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="crm-form-group">
                <label className="crm-label">Notes</label>
                <textarea
                    className="crm-textarea"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    disabled={loading}
                    rows={3}
                />
            </div>

            <button
                type="button"
                className="crm-btn crm-btn-link"
                onClick={() => setShowAdvanced((prev) => !prev)}
            >
                {showAdvanced ? 'Hide advanced fields' : 'Show advanced fields'}
            </button>

            {showAdvanced && (
                <div className="crm-advanced-section">
                    <div className="crm-form-row">
                        <div className="crm-form-group">
                            <label className="crm-label">Customer type</label>
                            <select
                                className="crm-select"
                                value={formData.customerType}
                                onChange={(e) => setFormData({ ...formData, customerType: e.target.value })}
                                disabled={loading}
                            >
                                <option value="">Select</option>
                                {customerTypeOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="crm-form-group">
                            <label className="crm-label">Lifecycle stage</label>
                            <select
                                className="crm-select"
                                value={formData.lifecycleStage}
                                onChange={(e) => setFormData({ ...formData, lifecycleStage: e.target.value })}
                                disabled={loading}
                            >
                                <option value="">Select</option>
                                {lifecycleOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="crm-form-row">
                        <div className="crm-form-group">
                            <label className="crm-label">Preferred channel</label>
                            <select
                                className="crm-select"
                                value={formData.preferredChannel}
                                onChange={(e) => setFormData({ ...formData, preferredChannel: e.target.value })}
                                disabled={loading}
                            >
                                <option value="">Select</option>
                                <option value="call">Call</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="email">Email</option>
                                <option value="in_person">In person</option>
                            </select>
                        </div>
                        <div className="crm-form-group">
                            <label className="crm-label">Language</label>
                            <input
                                className="crm-input"
                                value={formData.language}
                                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="crm-form-row">
                        <div className="crm-form-group">
                            <label className="crm-label">Rating (1-5)</label>
                            <input
                                type="number"
                                className="crm-input"
                                min="1"
                                max="5"
                                value={formData.rating}
                                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                                disabled={loading}
                            />
                        </div>
                        <div className="crm-form-group">
                            <label className="crm-label">Source</label>
                            <input
                                className="crm-input"
                                value={formData.source}
                                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="crm-form-row">
                        <div className="crm-form-group">
                            <label className="crm-label">Next follow-up</label>
                            <input
                                type="date"
                                className="crm-input"
                                value={formData.nextFollowUpAt}
                                onChange={(e) => setFormData({ ...formData, nextFollowUpAt: e.target.value })}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Purpose</label>
                        <select
                            className="crm-select"
                            value={formData.requirements.purpose}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    requirements: { ...formData.requirements, purpose: e.target.value },
                                })
                            }
                            disabled={loading}
                        >
                            <option value="">Select</option>
                            <option value="end_use">End use</option>
                            <option value="investment">Investment</option>
                            <option value="rental">Rental</option>
                        </select>
                    </div>

                    <div className="crm-form-row">
                        <div className="crm-form-group">
                            <label className="crm-label">Budget min</label>
                            <input
                                type="number"
                                className="crm-input"
                                value={formData.requirements.budgetMin}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        requirements: { ...formData.requirements, budgetMin: e.target.value },
                                    })
                                }
                                disabled={loading}
                            />
                        </div>
                        <div className="crm-form-group">
                            <label className="crm-label">Budget max</label>
                            <input
                                type="number"
                                className="crm-input"
                                value={formData.requirements.budgetMax}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        requirements: { ...formData.requirements, budgetMax: e.target.value },
                                    })
                                }
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Locations (comma separated)</label>
                        <input
                            className="crm-input"
                            value={requirementsText.locations}
                            onChange={(e) => setRequirementsText({ ...requirementsText, locations: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Projects (comma separated)</label>
                        <input
                            className="crm-input"
                            value={requirementsText.projects}
                            onChange={(e) => setRequirementsText({ ...requirementsText, projects: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Property types (comma separated)</label>
                        <input
                            className="crm-input"
                            value={requirementsText.propertyTypes}
                            onChange={(e) => setRequirementsText({ ...requirementsText, propertyTypes: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">BHK (comma separated)</label>
                        <input
                            className="crm-input"
                            value={requirementsText.bhk}
                            onChange={(e) => setRequirementsText({ ...requirementsText, bhk: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="crm-form-row">
                        <div className="crm-form-group">
                            <label className="crm-label">Area min (sqft)</label>
                            <input
                                type="number"
                                className="crm-input"
                                value={formData.requirements.areaMin}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        requirements: { ...formData.requirements, areaMin: e.target.value },
                                    })
                                }
                                disabled={loading}
                            />
                        </div>
                        <div className="crm-form-group">
                            <label className="crm-label">Area max (sqft)</label>
                            <input
                                type="number"
                                className="crm-input"
                                value={formData.requirements.areaMax}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        requirements: { ...formData.requirements, areaMax: e.target.value },
                                    })
                                }
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Timeline</label>
                        <select
                            className="crm-select"
                            value={formData.requirements.timeline}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    requirements: { ...formData.requirements, timeline: e.target.value },
                                })
                            }
                            disabled={loading}
                        >
                            <option value="">Select</option>
                            {timelineOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Must haves (comma separated)</label>
                        <input
                            className="crm-input"
                            value={requirementsText.mustHaves}
                            onChange={(e) => setRequirementsText({ ...requirementsText, mustHaves: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    <div className="crm-form-group">
                        <label className="crm-label">Deal breakers (comma separated)</label>
                        <input
                            className="crm-input"
                            value={requirementsText.dealBreakers}
                            onChange={(e) => setRequirementsText({ ...requirementsText, dealBreakers: e.target.value })}
                            disabled={loading}
                        />
                    </div>
                </div>
            )}

            <div className="crm-form-actions">
                <button
                    type="button"
                    className="crm-btn crm-btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="crm-btn crm-btn-primary"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save Contact'}
                </button>
            </div>
        </form>
    );
};

export default ContactForm;
